import './ViewSwitcher.scss';
import { FC } from 'react';
import clsx from 'clsx';
import { UnstyledButton } from '@mantine/core';
import { POPOVER_Z_INDEX } from '../../lib/zIndex';
import Tooltip from '../Tooltip';

export interface IViewSwitcherOption<T extends string> {
    /** Значение вида. */
    key: T;
    /** aria-label кнопки. */
    label: string;
    /** Подпись в тултипе. */
    title: string;
    /** Иконка вида. */
    Icon: FC;
    /** Вид сейчас недоступен: кнопка видна, подсказка (`title`) говорит почему, выбрать нельзя. */
    disabled?: boolean;
}

interface IViewSwitcherProps<T extends string> {
    value: T;
    options: IViewSwitcherOption<T>[];
    onChange: (view: T) => void;
    ariaLabel?: string;
    className?: string;
}

/** Сегментированный переключатель видов с «ползунком» primary-цвета. Generic над
 *  множеством видов — иконки и подписи задаются снаружи через `options`. */
function ViewSwitcher<T extends string>({ value, options, onChange, ariaLabel, className }: IViewSwitcherProps<T>) {
    const activeIdx = options.findIndex((o) => o.key === value);
    return (
        <div className={clsx('view-switcher', className)} role="radiogroup" aria-label={ariaLabel}>
            <span
                className="view-switcher__thumb"
                aria-hidden
                style={{ transform: `translateX(${Math.max(activeIdx, 0) * 100}%)` }}
            />
            {options.map((opt) => {
                const Icon = opt.Icon;
                const active = value === opt.key;
                return (
                    <Tooltip
                        key={opt.key}
                        variant="compact"
                        className="view-switcher__tooltip"
                        label={opt.title}
                        position="top"
                        openDelay={300}
                        // над окном, в котором переключатель
                        zIndex={POPOVER_Z_INDEX}
                        wrapTarget={false}
                        interactive={false}
                    >
                        <UnstyledButton
                            type="button"
                            role="radio"
                            aria-checked={active}
                            aria-label={opt.label}
                            // не disabled: у выключенной кнопки нет наведения, и подсказка с причиной не показалась бы
                            aria-disabled={opt.disabled || undefined}
                            className={clsx('view-switcher__btn', active && 'is-active', opt.disabled && 'is-disabled')}
                            onClick={opt.disabled ? undefined : () => onChange(opt.key)}
                        >
                            <Icon />
                        </UnstyledButton>
                    </Tooltip>
                );
            })}
        </div>
    );
}

export default ViewSwitcher;
